/* Client-side Rego checks (design eval F18).

   This is NOT a Rego parser or compiler, and the API has no validate or
   compile endpoint, so these are the structural checks that can be done
   honestly in the browser: a package declaration first, balanced braces /
   brackets / parentheses, terminated strings, imports in the right place, and
   a default / allow rule. Anything these pass can still fail on the server. */

export type RegoIssueSeverity = 'error' | 'warning';

export interface RegoIssue {
    severity: RegoIssueSeverity;
    /** 1-based line number. */
    line: number;
    /** 1-based column, when it points at a character. */
    column?: number;
    message: string;
}

const OPENERS: Record<string, string> = { '{': '}', '[': ']', '(': ')' };
const CLOSERS: Record<string, string> = { '}': '{', ']': '[', ')': '(' };
const PACKAGE_RE = /^package\s+[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*\s*(#.*)?$/;
const IMPORT_RE = /^import\s+(data|input|future|rego)(\.[A-Za-z_][A-Za-z0-9_]*|\[[^\]]+\])*(\s+as\s+[A-Za-z_][A-Za-z0-9_]*)?\s*(#.*)?$/;

/** Strip a trailing `# comment`, ignoring `#` inside strings. */
function codeOf(line: string): string {
    let inStr = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inStr) {
            if (ch === '\\') i++;
            else if (ch === '"') inStr = false;
        } else if (ch === '"') {
            inStr = true;
        } else if (ch === '#') {
            return line.slice(0, i);
        }
    }
    return line;
}

function checkBrackets(lines: string[]): RegoIssue[] {
    const issues: RegoIssue[] = [];
    const stack: { ch: string; line: number; column: number }[] = [];
    let raw: { line: number; column: number } | null = null; // inside a `raw string`

    for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        const ln = li + 1;
        let str: { column: number } | null = null; // "..." strings end on their line
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            const col = i + 1;
            if (raw) {
                if (ch === '`') raw = null;
                continue;
            }
            if (str) {
                if (ch === '\\') i++;
                else if (ch === '"') str = null;
                continue;
            }
            if (ch === '#') break;
            if (ch === '"') { str = { column: col }; continue; }
            if (ch === '`') { raw = { line: ln, column: col }; continue; }
            if (OPENERS[ch]) {
                stack.push({ ch, line: ln, column: col });
            } else if (CLOSERS[ch]) {
                const top = stack[stack.length - 1];
                if (!top) {
                    issues.push({ severity: 'error', line: ln, column: col, message: `Unexpected "${ch}" with nothing open.` });
                } else if (top.ch !== CLOSERS[ch]) {
                    issues.push({
                        severity: 'error', line: ln, column: col,
                        message: `"${ch}" closes "${top.ch}" opened on line ${top.line}; expected "${OPENERS[top.ch]}".`,
                    });
                    stack.pop();
                } else {
                    stack.pop();
                }
            }
        }
        if (str) {
            issues.push({ severity: 'error', line: ln, column: str.column, message: 'String is not closed on this line.' });
        }
    }
    if (raw) {
        issues.push({ severity: 'error', line: raw.line, column: raw.column, message: 'Raw string (`) is never closed.' });
    }
    for (const open of stack) {
        issues.push({ severity: 'error', line: open.line, column: open.column, message: `"${open.ch}" is never closed.` });
    }
    return issues;
}

export function checkRego(source: string): RegoIssue[] {
    const issues: RegoIssue[] = [];
    if (!source.trim()) {
        return [{ severity: 'error', line: 1, message: 'Rego content is empty.' }];
    }
    const lines = source.replace(/\r\n?/g, '\n').split('\n');

    // Package must be the first statement; imports follow it, before any rule.
    let packageLine = 0;
    let firstStatementLine = 0; // first import or rule
    let firstRuleLine = 0;
    let hasDefault = false;
    let hasAllow = false;
    for (let li = 0; li < lines.length; li++) {
        const ln = li + 1;
        const code = codeOf(lines[li]).trim();
        if (!code) continue;

        if (/^package\b/.test(code)) {
            if (packageLine) {
                issues.push({ severity: 'error', line: ln, message: `Second package declaration; the first is on line ${packageLine}.` });
                continue;
            }
            packageLine = ln;
            if (firstStatementLine) {
                issues.push({ severity: 'error', line: ln, message: `The package declaration must be the first statement (line ${firstStatementLine} comes before it).` });
            } else if (!PACKAGE_RE.test(code)) {
                issues.push({ severity: 'error', line: ln, message: 'Package name must be a dotted identifier, e.g. "package authz".' });
            }
            continue;
        }
        firstStatementLine = firstStatementLine || ln;
        if (/^import\b/.test(code)) {
            if (!IMPORT_RE.test(code)) {
                issues.push({ severity: 'error', line: ln, message: 'Import must start with data, input, future or rego, e.g. "import data.users".' });
            } else if (firstRuleLine) {
                issues.push({ severity: 'error', line: ln, message: `Imports must come before rules (first rule on line ${firstRuleLine}).` });
            }
            continue;
        }
        firstRuleLine = firstRuleLine || ln;

        const def = /^default\s+([A-Za-z_][A-Za-z0-9_]*)\s*(:?=)?\s*(.*)$/.exec(code);
        if (def) {
            hasDefault = true;
            if (def[1] === 'allow') hasAllow = true;
            if (!def[2] || !def[3].trim()) {
                issues.push({ severity: 'error', line: ln, message: `"default ${def[1]}" needs a value, e.g. "default ${def[1]} := false".` });
            }
            continue;
        }
        if (/^allow\b/.test(code)) hasAllow = true;
        if (/(^|[^=!<>:])(==|!=|:=|=|<=|>=|&&|\|\|)\s*$/.test(code) && !/[{[(,]\s*$/.test(code)) {
            // An expression that ends on a binary operator with nothing after it.
            const next = lines.slice(li + 1).find((l) => codeOf(l).trim());
            if (!next) issues.push({ severity: 'error', line: ln, message: 'Expression ends with an operator and nothing follows.' });
        }
    }

    if (!packageLine) {
        issues.unshift({ severity: 'error', line: 1, message: 'Missing package declaration, e.g. "package authz", as the first statement.' });
    }
    issues.push(...checkBrackets(lines));
    if (!hasAllow) {
        issues.push({ severity: 'warning', line: 1, message: 'No "allow" rule. Access decisions on this page are allow / deny; check what the server queries.' });
    }
    if (!hasDefault) {
        issues.push({ severity: 'warning', line: 1, message: 'No "default" rule. Without "default allow := false", allow is undefined when no rule matches.' });
    }
    return issues.sort((a, b) => a.line - b.line || (a.column ?? 0) - (b.column ?? 0));
}

export function hasRegoErrors(issues: RegoIssue[]): boolean {
    return issues.some((i) => i.severity === 'error');
}
